import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { supabase } from '../lib/supabase';

function PrivacyPolicy() {
  const [content, setContent] = useState<string | null>(null);
  const [title, setTitle] = useState('Privacy Policy');
  const [effectiveDate, setEffectiveDate] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDoc = async () => {
      const { data } = await supabase
        .from('legal_documents')
        .select('title, content, effective_date')
        .eq('type', 'privacy_policy')
        .eq('is_active', true)
        .order('effective_date', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (data) {
        setTitle(data.title);
        setContent(data.content);
        setEffectiveDate(data.effective_date);
      }
      setLoading(false);
    };

    fetchDoc();
  }, []);

  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex justify-center py-12">
          <div className="w-8 h-8 border-2 border-golf-orange border-t-transparent rounded-full animate-spin" />
        </div>
      );
    }

    if (content) {
      const html = DOMPurify.sanitize(marked(content) as string);
      return (
        <div
          className="prose prose-lg max-w-none"
          dangerouslySetInnerHTML={{ __html: html }}
        />
      );
    }

    return (
      <div className="prose prose-lg">
        <h2>Introduction</h2>
        <p>
          The Ibom Open ("we", "our", or "us") respects your privacy and is committed to protecting your personal data. This privacy policy explains how we collect, use, and protect your personal information when you use our website.
        </p>

        <h2>Information We Collect</h2>
        <p>We collect information that you provide directly to us, including:</p>
        <ul>
          <li>Name and contact information</li>
          <li>Registration details for tournaments</li>
          <li>Payment information</li>
          <li>Communication preferences</li>
        </ul>

        <h2>How We Use Your Information</h2>
        <p>We use your personal information to:</p>
        <ul>
          <li>Process your tournament registrations</li>
          <li>Send you important updates about the tournament</li>
          <li>Improve our services and website experience</li>
          <li>Comply with legal obligations</li>
        </ul>

        <h2>Cookies</h2>
        <p>
          We use cookies and similar tracking technologies to track activity on our website and store certain information. You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent.
        </p>

        <h2>Data Security</h2>
        <p>
          We implement appropriate security measures to protect your personal information. However, no method of transmission over the Internet is 100% secure, and we cannot guarantee absolute security.
        </p>

        <h2>Your Rights</h2>
        <p>Under GDPR, you have the right to:</p>
        <ul>
          <li>Access your personal data</li>
          <li>Correct inaccurate personal data</li>
          <li>Request erasure of your personal data</li>
          <li>Object to processing of your personal data</li>
          <li>Request restriction of processing your personal data</li>
          <li>Request transfer of your personal data</li>
          <li>Withdraw consent</li>
        </ul>

        <h2>Contact Us</h2>
        <p>
          For any questions about this Privacy Policy, please contact us at:
          <br />
          Email: privacy@ibomopen.com
          <br />
          Address: Ibom Golf &amp; Country Club, Uyo, Akwa Ibom State, Nigeria
        </p>
      </div>
    );
  };

  return (
    <div className="pt-20">
      <section className="py-16 bg-golf-sand">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto"
          >
            <h1 className="text-4xl md:text-5xl font-display font-bold text-golf-gray mb-8">
              {title}
            </h1>
            {effectiveDate && (
              <p className="text-gray-600 mb-4">
                Last updated: {new Date(effectiveDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
              </p>
            )}
            {!effectiveDate && !loading && (
              <p className="text-gray-600 mb-4">Last updated: March 15, 2025</p>
            )}
          </motion.div>
        </div>
      </section>

      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {renderContent()}
          </div>
        </div>
      </section>
    </div>
  );
}

export default PrivacyPolicy;
